import DOMPurify from 'dompurify';

const ALLOWED_IFRAME_ATTRIBUTES = [
    'src',
    'width',
    'height',
    'allow',
    'allowfullscreen',
    'frameborder',
    'loading',
    'referrerpolicy',
    'sandbox',
    'title',
];

const hasSafeSrc = (src: string | null) => {
    // Require an already-absolute http(s) URL. Parsing against a base would
    // let a relative ("/admin/dashboard") or protocol-relative
    // ("//evil.example.com") src resolve "successfully" and pass the
    // protocol check — real embed codes always ship a fully-qualified URL.
    if (!src || !/^https?:\/\//i.test(src)) return false;

    try {
        const url = new URL(src);

        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
};

/**
 * Returns sanitized `<iframe>` markup when `input` is exactly one iframe
 * element with a safe `src`, or `undefined` otherwise (not an iframe, more
 * than one top-level element, or an unsafe/missing `src`).
 */
export const sanitizeIframeHtml = (input: string): string | undefined => {
    const trimmed = input.trim();

    if (!/^<iframe[\s>]/i.test(trimmed)) return undefined;

    const parsed = new DOMParser().parseFromString(trimmed, 'text/html');
    const iframe = parsed.body.firstElementChild;

    if (
        !iframe ||
        iframe.tagName !== 'IFRAME' ||
        parsed.body.children.length !== 1
    ) {
        return undefined;
    }

    if (!hasSafeSrc(iframe.getAttribute('src'))) return undefined;

    const clean = DOMPurify.sanitize(iframe.outerHTML, {
        ALLOWED_TAGS: ['iframe'],
        ALLOWED_ATTR: ALLOWED_IFRAME_ATTRIBUTES,
    }).trim();

    // DOMPurify strips the element entirely if nothing safe remains.
    if (!/^<iframe[\s>]/i.test(clean)) return undefined;

    return clean;
};
